// Buying a plan: the four calls, and nothing else.
//
// THE CLIENT NEVER SENDS A PRICE. It sends a plan, a product and a billing state; the server
// computes every amount and freezes it on the order. The quote that comes back is shown
// VERBATIM — the sentences in it are the ones the customer has to read before paying, and
// rewording them here would mean the page and the order disagree about what was bought.

async function req(method, path, body) {
  const r = await fetch(path, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (r.status === 401) {
    window.location.replace(`/login?return=${encodeURIComponent('/billing')}`);
    throw new Error('Signed out');
  }
  const data = await r.json().catch(() => ({}));
  if (r.status === 202) return { preparing: true, retryAfter: data.retryAfter || 3 };
  if (!r.ok) {
    const err = new Error(data.message || data.error || `Request failed (${r.status})`);
    err.code = data.error;
    throw err;
  }
  return data;
}

export const loadPlans = () => req('GET', '/api/plans');
export const loadSubscription = () => req('GET', '/api/subscription');
export const quotePlan = ({ plan, product, billingState, gstin, legalName, address, promo }) =>
  req('POST', '/api/billing/quote', { plan, product, billingState, gstin, legalName, address, promo });
export const payOrder = ({ orderId, phone, method }) => req('POST', '/api/billing/pay', { orderId, phone, method });
// Changing plan: up is quoted like a purchase and paid with payOrder; down waits for the renewal.
export const quoteUpgrade = ({ plan, billingState, gstin, legalName, address }) =>
  req('POST', '/api/billing/quote-upgrade', { plan, billingState, gstin, legalName, address });
export const scheduleDowngrade = (plan) => req('POST', '/api/billing/downgrade', { plan });
// The lock-in offer that can come with a price rise: yearly at today's price.
export const quoteAnnualSwitch = ({ billingState, gstin, legalName, address }) => req('POST', '/api/billing/quote-annual-switch', { billingState, gstin, legalName, address });
export const cancelDowngrade = () => req('DELETE', '/api/billing/downgrade');
// Renewing a plan that does not renew on its own (prepaid): the next period, paid with payOrder.
export const quoteRenewal = ({ billingState, gstin, legalName, address }) => req('POST', '/api/billing/quote-renewal', { billingState, gstin, legalName, address });

/**
 * Pay, waiting out "another worker is preparing this" rather than surfacing it.
 *
 * A 202 means a second tab or a retry got there first and one session is already being made.
 * Polling is the whole point of the lease: the customer should end up on the SAME payment
 * page, not be handed a second one or shown an error for something that is working.
 */
export async function payWhenReady({ orderId, phone, method }, { tries = 5 } = {}) {
  for (let i = 0; i < tries; i++) {
    const out = await payOrder({ orderId, phone, method });
    if (!out.preparing) return out;
    await new Promise((r) => setTimeout(r, (out.retryAfter || 3) * 1000));
  }
  throw new Error('Your payment is still being prepared. Please try again in a moment.');
}

// Paise always as two digits (₹1,168.20, never ₹1,168.2); whole rupees without them (₹1,890).
export const INR = (n) => {
  const v = Number(n || 0);
  return '₹' + v.toLocaleString('en-IN', Number.isInteger(v) ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
